/**
 * Sidebar Component
 */

import React from 'react';
import { Layout, Menu, Drawer } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  DashboardOutlined,
  CheckCircleOutlined,
  ThunderboltOutlined,
  VideoCameraOutlined,
  DatabaseOutlined,
  CalendarOutlined,
  DollarOutlined,
  InboxOutlined,
  PartitionOutlined,
  SettingOutlined,
  CompassOutlined,
  FolderOutlined,
  PlaySquareOutlined,
  SafetyCertificateOutlined,
  ShopOutlined,
} from '@ant-design/icons';
import { isCurrentAuthUserAdmin } from '../../lib/authSession';

const { Sider } = Layout;

interface SidebarProps {
  collapsed: boolean;
  onCollapse?: (collapsed: boolean) => void;
  isMobile?: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({ collapsed, onCollapse, isMobile }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const activePath = location.pathname === '/app/dashboard' ? '/app' : location.pathname;
  const isAdmin = isCurrentAuthUserAdmin();

  const participantMenuItems = [
    { key: '/app', icon: <DashboardOutlined />, label: '오늘 할 일' },
    { key: '/app/content', icon: <ThunderboltOutlined />, label: '빠른 제작 & 보관함' },
    { key: '/app/product-studio', icon: <ShopOutlined />, label: 'AI 스튜디오 & 상세페이지' },
    { key: '/app/publishing-board', icon: <CalendarOutlined />, label: '발행 일정 캘린더' },
    { key: '/app/approval', icon: <CheckCircleOutlined />, label: '검수 대기실' },
    { key: '/app/campaign-planner', icon: <CompassOutlined />, label: '캠페인 전략' },
    { key: '/app/series-planner', icon: <PlaySquareOutlined />, label: '시리즈·숏폼 기획' },
    { key: '/app/projects', icon: <FolderOutlined />, label: '프로젝트 허브' },
    { key: '/app/settings', icon: <SettingOutlined />, label: '브랜드 & 내 설정' },
  ];

  const adminMenuItems = [
    ...participantMenuItems,
    { key: '/app/risk-review', icon: <SafetyCertificateOutlined />, label: '위험·광고법 검수' },
    { key: '/app/source-library', icon: <DatabaseOutlined />, label: '소스 라이브러리' },
    { key: '/app/workflow', icon: <ThunderboltOutlined />, label: '워크플로우' },
    { key: '/app/frameworks', icon: <PartitionOutlined />, label: '프레임워크' },
    { key: '/app/booth-intake', icon: <InboxOutlined />, label: '부스 접수함' },
    { key: '/app/revenue-ops', icon: <DollarOutlined />, label: '매출 운영' },
    { key: '/app/video', icon: <VideoCameraOutlined />, label: '영상 Beta' },
  ];
  const menuItems = isAdmin ? adminMenuItems : participantMenuItems;

  const handleMenuClick = ({ key }: { key: string }) => {
    navigate(key);
    if (isMobile && onCollapse) {
      onCollapse(true);
    }
  };

  const menuContent = (
    <>
      <div
        style={{
          height: '64px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--text-primary)',
          fontSize: collapsed && !isMobile ? '20px' : '18px',
          fontWeight: 'bold',
          background: 'var(--bg-secondary)',
        }}
      >
        {collapsed && !isMobile ? 'ST' : 'ST AI Marketing'}
      </div>
      <Menu
        theme="light"
        mode="inline"
        selectedKeys={[activePath]}
        items={menuItems}
        onClick={handleMenuClick}
      />
    </>
  );

  if (isMobile) {
    return (
      <Drawer
        placement="left"
        onClose={() => onCollapse && onCollapse(true)}
        open={!collapsed}
        width={240}
        styles={{ body: { padding: 0 } }}
        closable={false}
      >
        {menuContent}
      </Drawer>
    );
  }

  return (
    <Sider
      collapsible
      collapsed={collapsed}
      onCollapse={(value) => onCollapse && onCollapse(value)}
      breakpoint="lg"
      collapsedWidth="80"
      trigger={null}
      width={240}
      style={{
        overflow: 'auto',
        height: '100vh',
        position: 'fixed',
        left: 0,
        top: 0,
        bottom: 0,
        zIndex: 1001,
        background: 'var(--bg-tertiary)',
        borderRight: '1px solid var(--glass-border)',
      }}
    >
      {menuContent}
    </Sider>
  );
};

export default Sidebar;
